import { supabase } from "@/integrations/supabase/client";

export interface OrganizationFeatures {
  voice_agents: boolean;
  chat_assistants?: boolean; // Optional for backward compatibility, defaults to true
}

// Cache interface
interface CacheEntry {
  data: OrganizationFeatures;
  timestamp: number;
  ttl: number;
}

// In-memory cache for feature flags
class FeatureFlagCache {
  private cache = new Map<string, CacheEntry>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  set(
    key: string,
    data: OrganizationFeatures,
    ttl: number = this.DEFAULT_TTL,
  ): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get(key: string): OrganizationFeatures | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if entry has expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }
}

// Create a singleton instance
const featureCache = new FeatureFlagCache();

export const featureFlagService = {
  /**
   * Get cached feature flags synchronously (returns null if not cached)
   */
  getCachedFeatures(organizationId: string): OrganizationFeatures | null {
    return featureCache.get(organizationId);
  },

  /**
   * Check if voice agents feature is enabled for an organization (with caching)
   */
  async isVoiceAgentsEnabled(organizationId: string): Promise<boolean> {
    // Check cache first
    const cached = featureCache.get(organizationId);
    if (cached !== null) {
      return cached.voice_agents;
    }

    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("features")
        .eq("id", organizationId)
        .single();

      if (error) {
        console.error("Error fetching organization features:", error);
        return false; // Default to disabled if error
      }

      // Parse the features JSON or default to voice_agents: false
      const features = data?.features as unknown as OrganizationFeatures | null;
      const organizationFeatures = features ?? {
        voice_agents: false,
        chat_assistants: true,
      };

      // Cache the result
      featureCache.set(organizationId, organizationFeatures);

      return organizationFeatures.voice_agents;
    } catch (error) {
      console.error("Error checking voice agents feature:", error);
      return false; // Default to disabled if error
    }
  },

  /**
   * Get all features for an organization (with caching)
   */
  async getOrganizationFeatures(
    organizationId: string,
  ): Promise<OrganizationFeatures> {
    // Check cache first
    const cached = featureCache.get(organizationId);
    if (cached !== null) {
      return cached;
    }

    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("features")
        .eq("id", organizationId)
        .single();

      if (error) {
        console.error("Error fetching organization features:", error);
        const defaultFeatures = { voice_agents: false, chat_assistants: true };
        featureCache.set(organizationId, defaultFeatures);
        return defaultFeatures;
      }

      const features = data?.features as unknown as OrganizationFeatures | null;
      const organizationFeatures = features ?? {
        voice_agents: false,
        chat_assistants: true,
      };

      // Cache the result
      featureCache.set(organizationId, organizationFeatures);

      return organizationFeatures;
    } catch (error) {
      console.error("Error getting organization features:", error);
      const defaultFeatures = { voice_agents: false, chat_assistants: true };
      featureCache.set(organizationId, defaultFeatures);
      return defaultFeatures;
    }
  },

  /**
   * Check if chat assistants feature is enabled for an organization (with caching)
   */
  async isChatAssistantsEnabled(organizationId: string): Promise<boolean> {
    const features = await this.getOrganizationFeatures(organizationId);
    // Default to true if not explicitly set (backward compatibility)
    return features.chat_assistants ?? true;
  },

  /**
   * Clear cache for a specific organization (useful when features are updated)
   */
  clearCache(organizationId?: string): void {
    if (organizationId) {
      featureCache.delete(organizationId);
    } else {
      featureCache.clear();
    }
  },
};
